import { logger } from "../utils/logger";
import { useEffect, useState } from 'react';
import { getApiUrl } from '../utils/apiConfig';

// ============================================
// GTMScript — SIMPLIFIED
//
// GTM (GTM-NXL5SKD) and GA4 (G-DEF8777FZ1) are now loaded
// directly in index.html for maximum reliability.
//
// This component only handles:
// - Facebook Pixel (when configured in tracking settings)
// - LinkedIn Insight (when configured in tracking settings)
// - Consent event listener for future cookie banner integration
//
// If the /tracking/settings endpoint doesn't exist yet,
// this component does nothing — which is fine because
// GTM and GA4 are already firing from index.html.
// ============================================

interface TrackingSettings {
  facebookPixel?: {
    enabled: boolean;
    pixelId: string;
  };
  linkedInInsight?: {
    enabled: boolean;
    partnerId: string;
  };
  cookieConsent?: {
    enabled: boolean;
    mode: 'opt-in' | 'opt-out';
  };
}

export function GTMScript() {
  const [settings, setSettings] = useState<TrackingSettings | null>(null);
  const [consentGranted, setConsentGranted] = useState(false);

  useEffect(() => {
    // Defer loading of optional tracking scripts (Facebook, LinkedIn)
    // until the browser is idle — doesn't affect GTM/GA4 which
    // are already loaded from index.html.
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let idleCallbackId: number | null = null;

    const scheduleLoad = () => {
      if ('requestIdleCallback' in window) {
        idleCallbackId = (window as any).requestIdleCallback(
          () => loadTrackingSettings(),
          { timeout: 4000 }
        );
      } else {
        timerId = setTimeout(() => loadTrackingSettings(), 3000);
      }
    };

    scheduleLoad();

    // Listen for future cookie consent updates
    const handleConsent = (event: CustomEvent) => {
      if (event.detail.analytics || event.detail.marketing) {
        setConsentGranted(true);
      }
    };

    window.addEventListener('cookie-consent-updated' as any, handleConsent as any);

    return () => {
      if (timerId !== null) clearTimeout(timerId);
      if (idleCallbackId !== null && 'cancelIdleCallback' in window) {
        (window as any).cancelIdleCallback(idleCallbackId);
      }
      window.removeEventListener('cookie-consent-updated' as any, handleConsent as any);
    };
  }, []);

  const loadTrackingSettings = async () => {
    try {
      const response = await fetch(getApiUrl('/tracking/settings'));

      // If endpoint doesn't exist yet (returns HTML catch-all),
      // silently do nothing — GTM/GA4 already fire from index.html.
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return;

      const result = await response.json();
      if (result.success && result.data) {
        const trackingSettings = result.data;
        setSettings(trackingSettings);

        const consentMode    = trackingSettings.cookieConsent?.mode || 'opt-out';
        const consentEnabled = trackingSettings.cookieConsent?.enabled !== false;

        if (!consentEnabled || consentMode === 'opt-out') {
          loadOptionalScripts(trackingSettings);
        } else {
          const consent = localStorage.getItem('cookie_consent');
          if (consent) {
            try {
              const parsed = JSON.parse(consent);
              if (parsed.analytics || parsed.marketing) {
                setConsentGranted(true);
                loadOptionalScripts(trackingSettings);
              }
            } catch (e) {
              logger.error('[TRACKING] Error parsing consent:', e);
            }
          }
        }
      }
    } catch {
      // Silently ignore — GTM/GA4 already firing from index.html
    }
  };

  // Load scripts when consent is granted
  useEffect(() => {
    if (settings && consentGranted && !(window as any).fbPixelInjected) {
      loadOptionalScripts(settings);
    }
  }, [consentGranted, settings]);

  // Only Facebook Pixel and LinkedIn — GTM/GA4/Clarity handled elsewhere
  const loadOptionalScripts = (trackingSettings: TrackingSettings) => {
    if (trackingSettings.facebookPixel?.enabled && trackingSettings.facebookPixel.pixelId) {
      injectFacebookPixel(trackingSettings.facebookPixel.pixelId);
    }
    if (trackingSettings.linkedInInsight?.enabled && trackingSettings.linkedInInsight.partnerId) {
      injectLinkedInInsight(trackingSettings.linkedInInsight.partnerId);
    }
  };

  const injectFacebookPixel = (pixelId: string) => {
    if ((window as any).fbPixelInjected) return;
    (window as any).fbPixelInjected = true;

    const fbqScript = document.createElement('script');
    fbqScript.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(fbqScript);

    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  };

  const injectLinkedInInsight = (partnerId: string) => {
    if ((window as any).linkedinInsightInjected) return;
    (window as any).linkedinInsightInjected = true;

    (window as any)._linkedin_partner_id = partnerId;
    (window as any)._linkedin_data_partner_ids = (window as any)._linkedin_data_partner_ids || [];
    (window as any)._linkedin_data_partner_ids.push(partnerId);

    const script = document.createElement('script');
    script.innerHTML = `
      (function(l) {
        if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);})(window.lintrk);
    `;
    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.alt = '';
    img.src = `https://px.ads.linkedin.com/collect/?pid=${partnerId}&fmt=gif`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  };

  return null;
}